import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'

export default function SuperTrendQuantumBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0',
  onHeaderMouseDown,
}) {
  const dash = result?.dashboard ?? {}
  const hist = dash.history ?? {
    totalSignals: 0, tp1Rate: 0, tp2Rate: 0, slRate: 0, beRate: 0, avgR: 0,
  }
  const regime = dash.regime ?? 'NEUTRAL'
  const strength = dash.strength ?? 0
  const bullScore = dash.bullScore ?? 0
  const bearScore = dash.bearScore ?? 0

  const regimeColor =
    regime === 'BULL' ? '#35A07A' :
    regime === 'BEAR' ? '#B4574B' :
    '#B08D57'

  const latest = dash.latestSignal
  const active = dash.activeSignal

  function outcomeBadges(signal) {
    const badges = []
    if (signal?.tp1Hit) badges.push('TP1✓')
    if (signal?.tp2Hit) badges.push('TP2✓')
    if (signal?.slHit) badges.push('SL✗')
    if (signal?.beHit) badges.push('BE')
    return badges
  }

  function outcomeR(signal) {
    if (!signal) return null
    if (signal.outcome === 'TP2') return '+2R'
    if (signal.outcome === 'TP1') return '+1R'
    if (signal.outcome === 'BE') return '0R'
    if (signal.outcome === 'SL') return '−1R'
    return 'Abierta'
  }

  const activeLabel = active
    ? `${active.isBull ? 'LONG' : 'SHORT'} ${outcomeBadges(active).join(' ') || 'Abierta'}`
    : null

  const latestLabel = latest
    ? `${latest.isBull ? 'LONG' : 'SHORT'} ${outcomeBadges(latest).join(' ')} (${outcomeR(latest)})`
    : null

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[200px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          SuperTrend Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && (
            <span className="ml-1.5 text-[10px]" style={{ color: regimeColor }}>· {regime}</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        )}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Régimen</span>
              <span className="text-[11px] font-semibold" style={{ color: regimeColor }}>{regime}</span>
            </div>
          </div>

          <div className="flex flex-col gap-0.5 px-2.5 pb-1">
            <ScoreBar label="Bull" value={bullScore} color="#35A07A" />
            <ScoreBar label="Bear" value={bearScore} color="#B4574B" />
          </div>

          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Strength</span>
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>{(strength * 100).toFixed(1)}%</span>
            </div>
          </div>

          <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
            <div className="pt-1.5 pb-1 flex items-center justify-between">
              <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>
                {activeLabel ? 'Señal activa' : 'Sin señal activa'}
              </span>
              <span className="text-[10px] font-medium" style={{ color: active ? '#35A07A' : 'var(--ink, #15211B)' }}>
                {activeLabel || `Última: ${latestLabel || '—'}`}
              </span>
            </div>
          </div>

          <div className="px-2.5 pb-2" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
            <div className="pt-1.5 pb-1">
              <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>Historial</span>
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1">
              <div className="flex justify-between">
                <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Señales</span>
                <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{hist.totalSignals}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>R medio</span>
                <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{hist.avgR.toFixed(2)}R</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP1</span>
                <span className="text-[9px] font-medium" style={{ color: '#35A07A' }}>{(hist.tp1Rate * 100).toFixed(0)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>TP2</span>
                <span className="text-[9px] font-medium" style={{ color: '#35A07A' }}>{(hist.tp2Rate * 100).toFixed(0)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>SL</span>
                <span className="text-[9px] font-medium" style={{ color: '#B4574B' }}>{(hist.slRate * 100).toFixed(0)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>BE</span>
                <span className="text-[9px] font-medium" style={{ color: '#B08D57' }}>{(hist.beRate * 100).toFixed(0)}%</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-1 px-2.5 pb-2">
            {(dash.members ?? []).map(m => (
              <div key={m.id} className="flex flex-col items-center">
                <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>ST{m.id}</span>
                <span className={
                  m.dir === 'BULL' ? 'text-[#35A07A] text-[10px]' :
                  m.dir === 'BEAR' ? 'text-[#B4574B] text-[10px]' :
                  'text-[#B08D57] text-[10px]'
                }>
                  {m.dir === 'BULL' ? '▲' : m.dir === 'BEAR' ? '▼' : '—'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function ScoreBar({ label, value, color }) {
  const pct = Math.max(0, Math.min(100, (value || 0) * 100))
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] w-8" style={{ color: 'var(--ink, #15211B)' }}>{label}</span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(176, 141, 87, 0.18)' }}>
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-[10px] w-10 text-right" style={{ color: 'var(--ink, #15211B)' }}>{pct.toFixed(1)}%</span>
    </div>
  )
}
